import React, { useState, useEffect } from 'react';
import { 
  Scale, 
  Search, 
  MessageSquare, 
  Image, 
  CheckCircle2, 
  XCircle, 
  AlertOctagon, 
  ArrowRightLeft, 
  FileText, 
  Eye,
  Gavel,
  ShieldAlert
} from 'lucide-react';
import { StatusBadge } from '../components/common/StatusBadge';
import { Modal } from '../components/common/Modal';
import { ConfirmDialog } from '../components/common/ConfirmDialog';
import { DisputeCase, DisputeStatus } from '../types/admin';
import { AdminApiClient } from '../services/adminApiClient';

export const DisputesView: React.FC = () => {
  const [selectedDispute, setSelectedDispute] = useState<DisputeCase | null>(null);
  const [resolutionModalOpen, setResolutionModalOpen] = useState(false);
  const [resolutionType, setResolutionType] = useState<'full_refund' | 'full_payout' | 'split_payout'>('full_refund');
  const [splitRatio, setSplitRatio] = useState<number>(50); // % to buyer
  const [mediatorNotes, setMediatorNotes] = useState('');
  const [resolving, setResolving] = useState(false);

  // Sample real disputes reflecting buyer vs seller tribunal cases
  const [disputes, setDisputes] = useState<DisputeCase[]>([
    {
      id: 'dsp_case_001',
      orderId: 'ord_9821',
      orderNumber: 'ORD-9821',
      buyerId: 'usr_003_chioma',
      buyerName: 'Chioma Eze',
      sellerId: 'usr_005_emeka',
      sellerName: 'Emeka Johnson',
      disputedAmount: 45000,
      reason: 'Defective screen / display flickering not disclosed in listing photos.',
      description: 'The Samsung 27" Curved Monitor arrived with a noticeable green vertical flicker line down the right third of the display. Seller claimed it was in pristine 10/10 condition.',
      buyerEvidenceUrls: [
        'https://images.unsplash.com/photo-1527443224154-c4a3942d3acf?w=600&auto=format&fit=crop&q=80',
        'https://images.unsplash.com/photo-1593642702821-c8da6771f0c6?w=600&auto=format&fit=crop&q=80'
      ],
      sellerRebuttal: 'The monitor was 100% flawless when dispatched via local delivery rider. It appears the courier handled the package roughly during transit.',
      sellerEvidenceUrls: [
        'https://images.unsplash.com/photo-1585792180666-f7347c490ee2?w=600&auto=format&fit=crop&q=80'
      ],
      status: 'open',
      createdAt: 'Today at 07:15 AM',
      chatTranscript: [
        { id: 'c1', senderId: 'usr_003_chioma', senderName: 'Chioma', message: 'Hello, I just powered on the monitor and there is a huge vertical line on the screen!', timestamp: '07:05 AM', isBuyer: true },
        { id: 'c2', senderId: 'usr_005_emeka', senderName: 'Emeka', message: 'That is impossible, I tested it right before handing it to the bike rider.', timestamp: '07:10 AM', isBuyer: false },
        { id: 'c3', senderId: 'usr_003_chioma', senderName: 'Chioma', message: 'I have attached the pictures. I am filing a dispute to hold the escrow payment.', timestamp: '07:14 AM', isBuyer: true },
      ]
    },
    {
      id: 'dsp_case_002',
      orderId: 'ord_9799',
      orderNumber: 'ORD-9799',
      buyerId: 'usr_006_kemi',
      buyerName: 'Kemi Adeleke',
      sellerId: 'usr_002_tunde',
      sellerName: 'Tunde Bakare',
      disputedAmount: 22000,
      reason: 'Minor wear on leather cushion not described.',
      description: 'Vintage wooden chair had minor scratching on the rear leg and slight scuffing on the cushion corner.',
      buyerEvidenceUrls: [
        'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=600&auto=format&fit=crop&q=80'
      ],
      sellerRebuttal: 'Item was listed as "Good (Used Vintage)", not brand new. I am willing to offer a 20% discount if buyer keeps it.',
      status: 'under_review',
      createdAt: 'Yesterday at 02:40 PM',
      chatTranscript: [
        { id: 'c4', senderId: 'usr_006_kemi', senderName: 'Kemi', message: 'The chair is lovely but has scuffs on the back leg.', timestamp: '02:30 PM', isBuyer: true },
        { id: 'c5', senderId: 'usr_002_tunde', senderName: 'Tunde', message: 'Would you accept a ₦4,000 partial refund so we avoid returning it?', timestamp: '02:35 PM', isBuyer: false },
      ]
    }
  ]);

  useEffect(() => {
    AdminApiClient.getDisputes()
      .then((data) => {
        if (data && data.length > 0) {
          const mapped: DisputeCase[] = data.map((d: any) => ({
            id: d.id,
            orderId: d.orderId,
            orderNumber: d.order?.orderNumber || 'ORD-UNKNOWN',
            buyerId: d.order?.buyer?.id || '',
            buyerName: d.order?.buyer?.displayName || 'Buyer',
            sellerId: d.order?.seller?.id || '',
            sellerName: d.order?.seller?.displayName || 'Seller',
            disputedAmount: parseFloat(d.order?.totalAmount || '0'),
            reason: d.reason || 'Escrow dispute raised',
            description: d.description || '',
            buyerEvidenceUrls: d.buyerEvidence || [],
            sellerRebuttal: d.sellerRebuttal || '',
            sellerEvidenceUrls: d.sellerEvidence || [],
            status: d.status?.toLowerCase() === 'open' ? 'open' : d.status?.toLowerCase().includes('refund') ? 'resolved_buyer' : 'resolved_vendor',
            createdAt: new Date(d.createdAt).toLocaleDateString(),
            chatTranscript: [],
          }));
          setDisputes(mapped);
        }
      })
      .catch((err) => console.error('Failed to load disputes:', err));
  }, []);

  const handleResolveCase = async () => {
    if (!selectedDispute || !mediatorNotes.trim()) return;
    setResolving(true);
    try {
      const action = resolutionType === 'full_payout' ? 'RELEASE_TO_SELLER' : 'REFUND_TO_BUYER';
      await AdminApiClient.resolveDispute(selectedDispute.id, action, mediatorNotes).catch(console.error);

      const newStatus: DisputeStatus = resolutionType === 'full_refund' ? 'resolved_buyer' : resolutionType === 'full_payout' ? 'resolved_vendor' : 'split';
      
      setDisputes(prev => prev.map(d => {
        if (d.id === selectedDispute.id) {
          return {
            ...d,
            status: newStatus,
            resolutionType,
            mediatorNotes,
            resolvedAt: 'Just now'
          };
        }
        return d;
      }));

      setResolutionModalOpen(false);
      setSelectedDispute(null);
      setMediatorNotes('');
    } finally {
      setResolving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Tribunal Header Banner */}
      <div className="p-6 rounded-3xl border transition-colors flex flex-col md:flex-row items-start md:items-center justify-between gap-4
        bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c]
        light:bg-white light:border-[#D5E4DC] light:shadow-sm
      ">
        <div className="flex items-start gap-4">
          <div className="p-3.5 rounded-2xl bg-purple-500/15 border border-purple-500/30 text-purple-400 shrink-0">
            <Scale className="w-7 h-7" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-bold text-white dark:text-white light:text-[#0D1C17]">
                Dispute & Mediation Tribunal
              </h3>
              <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-purple-500/20 text-purple-300 border border-purple-500/30">
                {disputes.filter(d => d.status === 'open' || d.status === 'under_review').length} Pending
              </span>
            </div>
            <p className="mt-1 text-xs text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] max-w-2xl leading-relaxed">
              Super Admin judicial chamber for resolving escrow disputes. Review side-by-side photographic evidence, in-app buyer/seller chat records, and issue binding wallet verdicts (Buyer Refund, Vendor Payout, or Split Payout).
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 self-end md:self-auto">
          <span className="text-xs text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
            Target Resolution Time: &lt; 24 hrs
          </span>
        </div>
      </div>

      {/* Disputes Cases List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {disputes.map((d) => (
          <div 
            key={d.id} 
            className="rounded-3xl p-6 border transition-all flex flex-col justify-between
              bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c] hover:border-purple-500/30
              light:bg-white light:border-[#D5E4DC] light:shadow-sm
            "
          >
            <div>
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs font-bold text-brand-400">{d.orderNumber}</span>
                    <StatusBadge status={d.status} />
                  </div>
                  <h4 className="mt-2 text-base font-bold text-white dark:text-white light:text-[#0D1C17]">
                    {d.reason}
                  </h4>
                  <p className="text-xs text-brand-400 font-bold mt-1">
                    Disputed Escrow Value: ₦{d.disputedAmount.toLocaleString()}
                  </p>
                </div>
                <span className="text-[11px] text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                  {d.createdAt}
                </span>
              </div>

              {/* Opposing Parties */}
              <div className="mt-4 grid grid-cols-2 gap-3 p-3 rounded-2xl bg-[#10231d] dark:bg-[#10231d] light:bg-gray-50 border border-[#22493c] dark:border-[#22493c] light:border-gray-200 text-xs">
                <div>
                  <span className="text-[10px] uppercase font-bold text-brand-400">Buyer Claimant:</span>
                  <p className="font-semibold text-white dark:text-white light:text-[#0D1C17]">{d.buyerName}</p>
                </div>
                <div>
                  <span className="text-[10px] uppercase font-bold text-amber-400">Respondent Vendor:</span>
                  <p className="font-semibold text-white dark:text-white light:text-[#0D1C17]">{d.sellerName}</p>
                </div>
              </div>

              {/* Excerpt */}
              <p className="mt-4 text-xs text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] line-clamp-2">
                "{d.description}"
              </p>

              {/* Evidence previews */}
              <div className="mt-4 flex items-center gap-2">
                <div className="flex items-center gap-1.5 text-xs text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                  <Image className="w-3.5 h-3.5 text-brand-400" />
                  <span>{d.buyerEvidenceUrls.length} Photo Proof(s)</span>
                </div>
                <span className="text-[#90cbb7]">•</span>
                <div className="flex items-center gap-1.5 text-xs text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                  <MessageSquare className="w-3.5 h-3.5 text-cyan-400" />
                  <span>{d.chatTranscript?.length || 0} Chat Messages</span>
                </div>
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-3 pt-4 border-t border-[#22493c] dark:border-[#22493c] light:border-[#EBF2EE]">
              <button
                onClick={() => {
                  setSelectedDispute(d);
                }}
                className="flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold text-white bg-[#22493c] hover:bg-[#2a5547] transition-colors"
              >
                <Eye className="w-3.5 h-3.5" />
                <span>Inspect Evidence Dossier</span>
              </button>

              {d.status === 'open' || d.status === 'under_review' ? (
                <button
                  onClick={() => {
                    setSelectedDispute(d);
                    setResolutionModalOpen(true);
                  }}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold text-forest-void bg-brand-500 hover:bg-brand-400 shadow-glow-sm transition-all"
                >
                  <Gavel className="w-3.5 h-3.5" />
                  <span>Deliver Verdict</span>
                </button>
              ) : (
                <span className="text-xs font-semibold text-emerald-400">
                  Case Settled ({d.resolutionType})
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Full Evidence & Chat Dossier Modal */}
      {selectedDispute && !resolutionModalOpen && (
        <Modal
          isOpen={Boolean(selectedDispute)}
          onClose={() => setSelectedDispute(null)}
          title={`Tribunal Case File: ${selectedDispute.orderNumber}`}
          subtitle={`Escrow Stake: ₦${selectedDispute.disputedAmount.toLocaleString()}`}
          maxWidth="4xl"
        >
          <div className="space-y-6 text-xs">
            {/* Side-by-side statements */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Buyer Box */}
              <div className="p-4 rounded-2xl bg-[#10231d] dark:bg-[#10231d] light:bg-gray-50 border border-[#22493c] dark:border-[#22493c] light:border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold text-brand-400 uppercase text-[11px]">
                    Buyer Claim ({selectedDispute.buyerName})
                  </span>
                  <span className="text-[10px] text-[#90cbb7] light:text-gray-500">Claimant</span>
                </div>
                <p className="text-white dark:text-white light:text-[#0D1C17] leading-relaxed">
                  {selectedDispute.description}
                </p>

                {selectedDispute.buyerEvidenceUrls.length > 0 && (
                  <div className="mt-3">
                    <p className="text-[10px] text-[#90cbb7] light:text-gray-500 mb-2">Buyer Photo Evidence:</p>
                    <div className="flex gap-2 overflow-x-auto pb-1">
                      {selectedDispute.buyerEvidenceUrls.map((url, i) => (
                        <a key={i} href={url} target="_blank" rel="noreferrer" className="shrink-0 w-24 h-24 rounded-xl overflow-hidden border border-[#22493c] hover:border-brand-500 transition-colors">
                          <img src={url} alt={`Buyer evidence ${i+1}`} className="w-full h-full object-cover" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Vendor Box */}
              <div className="p-4 rounded-2xl bg-[#10231d] dark:bg-[#10231d] light:bg-gray-50 border border-[#22493c] dark:border-[#22493c] light:border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold text-amber-400 uppercase text-[11px]">
                    Vendor Rebuttal ({selectedDispute.sellerName})
                  </span>
                  <span className="text-[10px] text-[#90cbb7] light:text-gray-500">Respondent</span>
                </div>
                <p className="text-white dark:text-white light:text-[#0D1C17] leading-relaxed">
                  {selectedDispute.sellerRebuttal || 'No rebuttal submitted yet.'}
                </p>

                {selectedDispute.sellerEvidenceUrls && selectedDispute.sellerEvidenceUrls.length > 0 && (
                  <div className="mt-3">
                    <p className="text-[10px] text-[#90cbb7] light:text-gray-500 mb-2">Seller Dispatch Proof:</p>
                    <div className="flex gap-2 overflow-x-auto pb-1">
                      {selectedDispute.sellerEvidenceUrls.map((url, i) => (
                        <a key={i} href={url} target="_blank" rel="noreferrer" className="shrink-0 w-24 h-24 rounded-xl overflow-hidden border border-[#22493c] hover:border-amber-500 transition-colors">
                          <img src={url} alt={`Seller evidence ${i+1}`} className="w-full h-full object-cover" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* In-App Chat Transcript */}
            <div>
              <h5 className="font-bold text-white dark:text-white light:text-gray-900 mb-3 flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-cyan-400" />
                <span>Buyer-Seller Unaltered Chat Transcript</span>
              </h5>
              
              <div className="p-4 rounded-2xl bg-[#10231d] dark:bg-[#10231d] light:bg-gray-100 border border-[#22493c] dark:border-[#22493c] space-y-3 max-h-56 overflow-y-auto">
                {selectedDispute.chatTranscript?.map((msg) => (
                  <div key={msg.id} className={`flex flex-col ${msg.isBuyer ? 'items-start' : 'items-end'}`}>
                    <div className="flex items-center gap-1.5 mb-0.5 text-[10px] text-[#90cbb7] dark:text-[#90cbb7] light:text-gray-500">
                      <span className="font-bold">{msg.senderName}</span>
                      <span>({msg.isBuyer ? 'Buyer' : 'Seller'})</span>
                      <span>• {msg.timestamp}</span>
                    </div>
                    <div className={`p-2.5 rounded-2xl max-w-sm text-xs ${msg.isBuyer ? 'bg-[#22493c] text-white rounded-tl-none' : 'bg-brand-500/20 text-brand-300 border border-brand-500/30 rounded-tr-none'}`}>
                      {msg.message}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Bottom Actions */}
            <div className="pt-4 border-t border-[#22493c] dark:border-[#22493c] flex justify-between items-center">
              <button
                onClick={() => setSelectedDispute(null)}
                className="px-4 py-2 rounded-full text-xs font-bold text-white bg-[#22493c] hover:bg-[#2a5547]"
              >
                Close Case File
              </button>

              {(selectedDispute.status === 'open' || selectedDispute.status === 'under_review') && (
                <button
                  onClick={() => setResolutionModalOpen(true)}
                  className="px-5 py-2 rounded-full text-xs font-bold text-forest-void bg-brand-500 hover:bg-brand-400 shadow-glow-sm"
                >
                  Proceed to Deliver Verdict
                </button>
              )}
            </div>
          </div>
        </Modal>
      )}

      {/* Deliver Verdict Modal */}
      {selectedDispute && resolutionModalOpen && (
        <Modal
          isOpen={resolutionModalOpen}
          onClose={() => setResolutionModalOpen(false)}
          title={`Deliver Binding Ruling: ${selectedDispute.orderNumber}`}
          subtitle={`Disputed Amount: ₦${selectedDispute.disputedAmount.toLocaleString()}`}
          maxWidth="lg"
        >
          <div className="space-y-5 text-xs">
            <div>
              <label className="block text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-2">
                Ruling Option:
              </label>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <button
                  type="button"
                  onClick={() => setResolutionType('full_refund')}
                  className={`p-3 rounded-2xl border text-center transition-all ${resolutionType === 'full_refund' ? 'border-brand-500 bg-brand-500/15 text-brand-400 font-bold' : 'border-[#22493c] text-[#90cbb7] hover:border-brand-500/40'}`}
                >
                  <CheckCircle2 className="w-5 h-5 mx-auto mb-1.5 text-brand-400" />
                  <span>Rule for Buyer</span>
                  <p className="text-[10px] opacity-75 mt-0.5">100% Refund</p>
                </button>

                <button
                  type="button"
                  onClick={() => setResolutionType('full_payout')}
                  className={`p-3 rounded-2xl border text-center transition-all ${resolutionType === 'full_payout' ? 'border-amber-500 bg-amber-500/15 text-amber-400 font-bold' : 'border-[#22493c] text-[#90cbb7] hover:border-amber-500/40'}`}
                >
                  <CheckCircle2 className="w-5 h-5 mx-auto mb-1.5 text-amber-400" />
                  <span>Rule for Vendor</span>
                  <p className="text-[10px] opacity-75 mt-0.5">100% Payout (-8.06%)</p>
                </button>

                <button
                  type="button"
                  onClick={() => setResolutionType('split_payout')}
                  className={`p-3 rounded-2xl border text-center transition-all ${resolutionType === 'split_payout' ? 'border-cyan-500 bg-cyan-500/15 text-cyan-400 font-bold' : 'border-[#22493c] text-[#90cbb7] hover:border-cyan-500/40'}`}
                >
                  <ArrowRightLeft className="w-5 h-5 mx-auto mb-1.5 text-cyan-400" />
                  <span>Split Payout</span>
                  <p className="text-[10px] opacity-75 mt-0.5">Custom Split</p>
                </button>
              </div>
            </div>

            {resolutionType === 'split_payout' && (
              <div className="p-4 rounded-2xl bg-[#10231d] dark:bg-[#10231d] light:bg-gray-50 border border-[#22493c] dark:border-[#22493c]">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-[#90cbb7] light:text-gray-600">Buyer Refund: {splitRatio}% (₦{((selectedDispute.disputedAmount * splitRatio) / 100).toLocaleString()})</span>
                  <span className="text-[#90cbb7] light:text-gray-600">Seller Payout: {100 - splitRatio}% (₦{((selectedDispute.disputedAmount * (100 - splitRatio)) / 100).toLocaleString()})</span>
                </div>
                <input
                  type="range"
                  min={10}
                  max={90}
                  step={5}
                  value={splitRatio}
                  onChange={(e) => setSplitRatio(Number(e.target.value))}
                  className="w-full accent-brand-500"
                />
              </div>
            )}

            <div>
              <label className="block text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1.5">
                Mandatory Judicial Mediator Findings & Case Rationale:
              </label>
              <textarea
                value={mediatorNotes}
                onChange={(e) => setMediatorNotes(e.target.value)}
                placeholder="Detail the conclusive findings (e.g. verified defect from photos, packaging was undamaged, settlement split agreed by both parties)..."
                rows={3}
                className="w-full p-3 rounded-2xl text-xs outline-none border transition-colors
                  bg-[#10231d] dark:bg-[#10231d] border-[#22493c] dark:border-[#22493c] text-white
                  light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17]
                  focus:border-brand-500"
              />
            </div>

            <div className="pt-4 border-t border-[#22493c] dark:border-[#22493c] flex justify-end gap-3">
              <button
                onClick={() => setResolutionModalOpen(false)}
                className="px-4 py-2 rounded-full text-xs font-bold text-white bg-[#22493c] hover:bg-[#2a5547]"
              >
                Cancel
              </button>
              <button
                onClick={handleResolveCase}
                disabled={!mediatorNotes.trim() || resolving}
                className="px-5 py-2 rounded-full text-xs font-bold text-forest-void bg-brand-500 hover:bg-brand-400 shadow-glow-sm disabled:opacity-50"
              >
                {resolving ? 'Executing...' : 'Execute Binding Verdict'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};
